import { useState } from 'react';
import { cn } from '@/lib/cn';

interface LogoAvatarProps {
  /** URL pública del logo. Si es null/vacío o falla la carga, cae a iniciales. */
  src?: string | null;
  /** Nombre usado para generar iniciales. */
  name: string;
  size?: number;
  className?: string;
  /** 'square' (radios medianos, para empresas) o 'circle' (para users). */
  shape?: 'square' | 'circle';
}

/**
 * Muestra el logo de una entidad. Si no hay logo (o la imagen falla),
 * renderea un placeholder con las iniciales del nombre sobre el azul institucional.
 */
export function LogoAvatar({
  src,
  name,
  size = 32,
  className,
  shape = 'square',
}: LogoAvatarProps) {
  const [failed, setFailed] = useState(false);
  const rounded = shape === 'circle' ? 'rounded-full' : 'rounded-md';

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={name}
        onError={() => setFailed(true)}
        className={cn(
          'flex-none bg-white object-contain p-0.5 shadow-sm',
          rounded,
          className,
        )}
        style={{ width: size, height: size }}
        draggable={false}
      />
    );
  }

  return (
    <span
      className={cn(
        'grid flex-none place-items-center bg-gradient-to-br from-blue to-blue-soft font-bold text-white',
        rounded,
        className,
      )}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }}
      aria-label={name}
    >
      {initialsOf(name)}
    </span>
  );
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean).slice(0, 2);
  if (parts.length === 0) return '·';
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '·';
}
